// @ts-strict-ignore
import { Columns } from "@fullscript/aviary-web";
import { faInfoCircle } from "@fullscript/pro-regular-svg-icons";
import { Trans, useTranslation } from "react-i18next";

import { FloatingLabelInput, PhoneInput } from "@aviary";
import { removePlusSign } from "@aviary/components/PhoneInput/PhoneInput";
import { IconWrapper } from "@shared/components/IconWrapper/IconWrapper";
import { StateSelect } from "@shared/components/StateSelect/StateSelect";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import type {
  AddressInformationErrors,
  PartialAddressInformationData,
} from "@shared/types/fields/addressInformationTypes";
import { l } from "@unauthenticated/oauth/locales/i18n";

import * as styles from "./PartialAddressFields.styles";

interface Props {
  addressInformationErrors: AddressInformationErrors;
  addressInformationData: PartialAddressInformationData;
  handleSetAddressInformation: (addressInfo: PartialAddressInformationData) => void;
}

const PartialAddressFields = ({
  addressInformationErrors,
  addressInformationData,
  handleSetAddressInformation,
}: Props) => {
  const { t } = useTranslation();
  const { isCanada } = useSharedGlobalConfig();

  const {
    region: regionErrors,
    postalCode: postalCodeErrors,
    phone: phoneErrors,
  } = addressInformationErrors !== null &&
  !!addressInformationErrors.fields &&
  addressInformationErrors.fields;

  const updateAddressInfo = (id, value) => {
    handleSetAddressInformation({ ...addressInformationData, [id]: value });
  };

  const onAddressChange = e => {
    const value = e.target.value;
    const id = e.target.id;
    updateAddressInfo(id, value);
  };

  const onPhoneNumberChange = value => {
    updateAddressInfo("phone", removePlusSign(value));
  };

  const regionLabel = isCanada ? t(l.oauth.Province) : t(l.oauth.State);

  const postalCodeLabel = isCanada ? t(l.oauth.PostalCode) : t(l.oauth.ZipCode);

  const renderPhoneTooltip = () => {
    return (
      <Trans i18nKey={l.oauth.PhoneTooltip}>
        We're here to help. Providing this information helps our team give our users a{" "}
        <strong>faster</strong> support experience
      </Trans>
    );
  };

  return (
    <Columns>
      <Columns.Column columnWidth={6}>
        <StateSelect
          data-testid="state-select"
          id="region"
          onChange={onAddressChange}
          value={addressInformationData?.region ?? ""}
          label={regionLabel}
          name={regionLabel}
          placeholder={t(l.oauth.Select)}
          isRequired
          errors={regionErrors}
        />
      </Columns.Column>
      <Columns.Column columnWidth={6}>
        <FloatingLabelInput
          type="text"
          id="postalCode"
          label={postalCodeLabel}
          name={postalCodeLabel}
          value={addressInformationData?.postalCode ?? ""}
          handleChange={onAddressChange}
          required
          errors={postalCodeErrors}
        />
      </Columns.Column>
      <Columns.Column columnWidth={6}>
        <IconWrapper icon={faInfoCircle} tooltipMessage={renderPhoneTooltip()}>
          <div css={styles.phoneInput}>
            <PhoneInput
              id="phone"
              label={t(l.oauth.PhoneNumber)}
              name={t(l.oauth.PhoneNumber)}
              aria-label={t(l.oauth.PhoneNumber)}
              value={addressInformationData?.phone ?? ""}
              onChange={onPhoneNumberChange}
              required
              errors={phoneErrors}
              preferredCountryCode={isCanada ? "CA" : "US"}
            />
          </div>
        </IconWrapper>
      </Columns.Column>
    </Columns>
  );
};

export { PartialAddressFields };
